import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Grid3X3, LayoutGrid } from 'lucide-react';

export default function MatrizNotas({ year, trimestre, turmaId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ matriz: [], colunas: [] });
  const [modo, setModo] = useState('disciplina');
  
  useEffect(() => {
    loadData();
  }, [year, trimestre, turmaId, modo]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year, modo };
      if (trimestre) params.trimestre = trimestre;
      if (turmaId) params.turma_id = turmaId;
      
      const res = await api.get('/central-notas/matriz-notas', { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar matriz:', error);
      toast.error('Erro ao carregar matriz');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-white border border-purple-200/50">
        <CardContent className="py-8">
          <Skeleton className="h-[400px] bg-slate-100" />
        </CardContent>
      </Card>
    );
  }
  
  const filteredColunas = (data.colunas || []).filter(c => !c.nome?.toLowerCase().includes('bimestral'));
  const bimestralIndexes = new Set((data.colunas || []).map((c, i) => c.nome?.toLowerCase().includes('bimestral') ? i : -1).filter(i => i >= 0));

  return (
    <div className="space-y-4" data-testid="matriz-notas">
      {/* Header */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Grid3X3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Mapa Inteligente de Notas</h3>
                <p className="text-sm text-slate-500">{data.total_alunos} alunos • {filteredColunas.length} {modo === 'disciplina' ? 'disciplinas' : 'tipos'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={modo === 'disciplina' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModo('disciplina')}
                className={modo === 'disciplina' ? 'bg-purple-500 hover:bg-purple-600' : 'border-slate-200'}
              >
                Por Disciplina
              </Button>
              <Button
                variant={modo === 'tipo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModo('tipo')}
                className={modo === 'tipo' ? 'bg-purple-500 hover:bg-purple-600' : 'border-slate-200'}
              >
                Por Tipo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Matriz */}
      <Card className="bg-white border border-purple-200/50 shadow-sm overflow-hidden">
        <div className="overflow-x-auto dna-responsive-table sticky-first-col">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 min-w-[200px]">
                  Aluno
                </th>
                {filteredColunas.map((col) => (
                  <th key={col.id} className="px-3 py-3 text-center font-semibold text-slate-700 min-w-[80px]">
                    <span className="truncate block max-w-[100px]" title={col.nome}>
                      {col.nome?.length > 12 ? col.nome.substring(0, 12) + '...' : col.nome}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-purple-50">
                  Média
                </th>
              </tr>
            </thead>
            <tbody>
              {data.matriz?.length === 0 ? (
                <tr>
                  <td colSpan={(filteredColunas.length) + 2} className="px-4 py-12 text-center text-slate-500">
                    Nenhum dado encontrado
                  </td>
                </tr>
              ) : (
                data.matriz?.map((row, idx) => (
                  <tr key={row.student_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="sticky left-0 bg-white px-4 py-2">
                      <div className="flex items-center gap-2">
                        <img src={row.foto || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-7 h-7 rounded-full object-cover" />
                        <div>
                          <p className="font-medium text-slate-800 text-xs truncate max-w-[150px]">{row.nome}</p>
                          <p className="text-[10px] text-slate-400">{row.turma}</p>
                        </div>
                      </div>
                    </td>
                    {filteredColunas.map((col) => {
                      const celula = row.celulas?.[col.id];
                      return (
                        <td 
                          key={col.id} 
                          className="px-3 py-2 text-center"
                          style={{ backgroundColor: celula?.color ? `${celula.color}15` : 'transparent' }}
                        >
                          {celula ? (
                            <span 
                              className="font-semibold"
                              style={{ color: celula.color }}
                              title={`${celula.qtd_notas} nota(s)`}
                            >
                              {celula.valor?.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center bg-purple-50/50">
                      <span className={`text-lg font-bold ${
                        row.media_geral >= 7 ? 'text-green-600' :
                        row.media_geral >= 5 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {row.media_geral?.toFixed(1)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Legenda */}
      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span className="text-slate-600">≥ 7.0 Satisfatório</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500" />
          <span className="text-slate-600">5.0 - 6.9 Atenção</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500" />
          <span className="text-slate-600">&lt; 5.0 Crítico</span>
        </div>
      </div>
    </div>
  );
}
